import { useMemo, useState } from 'react';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

// Deterministic palette keyed off user_id index. Distinctive but harmonious.
const PALETTE = [
  '#007AFF', '#E00000', '#007A33', '#4338CA', '#FFB81C', '#34C759',
  '#FF3B30', '#AF52DE', '#FF9500', '#5AC8FA', '#5856D6', '#FF2D55',
];

function colorForUser(userId, index) {
  return PALETTE[index % PALETTE.length];
}

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'Australia/Sydney' });
}

/**
 * Progression chart for a single tab of the leaderboards page.
 *
 * @param {Array} series - list of {date, label, sport?, per_user: {uid: pts}}
 * @param {Array} users  - [{user_id, name}]
 * @param {string} sportKey - 'combined' | 'afl' | 'nrl' | 'wc' (for testids only)
 */
export default function LeaderboardProgressionChart({ series, users, sportKey, active = true }) {
  const [viewMode, setViewMode] = useState('cumulative'); // 'cumulative' | 'per_round'
  const [hidden, setHidden] = useState({}); // {user_id: true}

  const { chartData, userMeta } = useMemo(() => {
    if (!series || series.length === 0) {
      return { chartData: [], userMeta: [] };
    }
    // Assign each user a stable colour by their order in `users`.
    const meta = users.map((u, i) => ({
      user_id: u.user_id,
      name: u.name,
      color: colorForUser(u.user_id, i),
    }));

    // Track running totals for cumulative computation
    const running = Object.fromEntries(users.map(u => [u.user_id, 0]));
    const data = series.map((entry, idx) => {
      const row = {
        idx,
        date: entry.date,
        dateLabel: fmtDate(entry.date),
        roundLabel: entry.label,
        sport: entry.sport || null,
      };
      users.forEach(u => {
        const gained = entry.per_user?.[u.user_id] || 0;
        running[u.user_id] += gained;
        row[`${u.user_id}__cum`] = running[u.user_id];
        row[`${u.user_id}__pr`] = gained;
      });
      return row;
    });
    return { chartData: data, userMeta: meta };
  }, [series, users]);

  // Skip rendering Recharts on inactive Radix tabs — they render with 0 size
  // and log 'width/height should be greater than 0' warnings.
  if (!active) return null;

  const visibleUsers = userMeta.filter(u => !hidden[u.user_id]);

  const toggleUser = (uid) => setHidden(prev => ({ ...prev, [uid]: !prev[uid] }));

  const yKey = viewMode === 'cumulative' ? '__cum' : '__pr';

  const emptyState = !chartData || chartData.length === 0;

  return (
    <div className="tipster-card mb-6" data-testid={`progression-chart-${sportKey}`}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h3 className="text-sm font-bold uppercase tracking-wider text-[#1A1A1A]">Points Progression</h3>
          <p className="text-xs text-gray-400 mt-0.5">
            {viewMode === 'cumulative' ? 'Running total per tipster across the season' : 'Points earned per round'}
          </p>
        </div>
        <div className="flex items-center gap-1 bg-gray-100 rounded-sm p-0.5" role="tablist">
          <button
            onClick={() => setViewMode('cumulative')}
            className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-sm transition-all ${
              viewMode === 'cumulative' ? 'bg-white text-[#007AFF] shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid={`progression-view-cumulative-${sportKey}`}
          >
            Cumulative
          </button>
          <button
            onClick={() => setViewMode('per_round')}
            className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-sm transition-all ${
              viewMode === 'per_round' ? 'bg-white text-[#007AFF] shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid={`progression-view-per-round-${sportKey}`}
          >
            Per Round
          </button>
        </div>
      </div>

      {emptyState ? (
        <div className="py-12 text-center text-sm text-gray-400" data-testid={`progression-empty-${sportKey}`}>
          No completed rounds to plot yet.
        </div>
      ) : (
        <>
          <div style={{ width: '100%', height: 320 }} data-testid={`progression-canvas-${sportKey}`}>
            <ResponsiveContainer>
              {viewMode === 'cumulative' ? (
                <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                  <XAxis
                    dataKey="dateLabel"
                    tick={{ fontSize: 11, fill: '#8E8E93' }}
                    axisLine={{ stroke: '#E5E5EA' }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#8E8E93' }}
                    axisLine={{ stroke: '#E5E5EA' }}
                    tickLine={false}
                    width={38}
                  />
                  <Tooltip content={<PointsTooltip userMeta={userMeta} yKey={yKey} />} />
                  {visibleUsers.map(u => (
                    <Line
                      key={u.user_id}
                      type="monotone"
                      dataKey={`${u.user_id}${yKey}`}
                      name={u.name}
                      stroke={u.color}
                      strokeWidth={2.25}
                      dot={{ r: 2.5, strokeWidth: 0, fill: u.color }}
                      activeDot={{ r: 5 }}
                      isAnimationActive={false}
                    />
                  ))}
                </LineChart>
              ) : (
                <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                  <XAxis
                    dataKey="dateLabel"
                    tick={{ fontSize: 11, fill: '#8E8E93' }}
                    axisLine={{ stroke: '#E5E5EA' }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#8E8E93' }}
                    axisLine={{ stroke: '#E5E5EA' }}
                    tickLine={false}
                    width={38}
                  />
                  <Tooltip content={<PointsTooltip userMeta={userMeta} yKey={yKey} />} />
                  {visibleUsers.map(u => (
                    <Bar
                      key={u.user_id}
                      dataKey={`${u.user_id}${yKey}`}
                      name={u.name}
                      fill={u.color}
                      isAnimationActive={false}
                    />
                  ))}
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>

          {/* Clickable legend */}
          <div className="flex flex-wrap gap-2 mt-3">
            {userMeta.map(u => {
              const off = hidden[u.user_id];
              return (
                <button
                  key={u.user_id}
                  onClick={() => toggleUser(u.user_id)}
                  className={`flex items-center gap-2 px-2.5 py-1 rounded-sm border text-xs transition-all ${
                    off
                      ? 'border-gray-200 bg-white text-gray-400 line-through'
                      : 'border-gray-200 bg-gray-50 text-[#1A1A1A]'
                  }`}
                  title={off ? `Show ${u.name}` : `Hide ${u.name}`}
                  data-testid={`progression-legend-${sportKey}-${u.user_id}`}
                >
                  <span
                    className="w-3 h-3 rounded-sm inline-block"
                    style={{ backgroundColor: off ? '#D1D5DB' : u.color }}
                  />
                  <span className="font-semibold">{u.name}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function PointsTooltip({ active, payload, label, userMeta, yKey }) {
  if (!active || !payload || payload.length === 0) return null;
  // Get the first payload item's original row (all items share it)
  const row = payload[0]?.payload || {};
  const suffixLen = yKey.length; // e.g. '__cum' or '__pr'
  // Build user_id -> value from the row directly so hidden series still show.
  const entries = userMeta
    .map(u => ({
      name: u.name,
      color: u.color,
      value: row[`${u.user_id}${yKey}`] ?? 0,
    }))
    .filter(x => x.value !== 0 || yKey === '__cum')
    .sort((a, b) => b.value - a.value);
  return (
    <div className="bg-white border border-gray-200 rounded-sm shadow-lg p-3 text-xs" style={{ minWidth: 180 }}>
      <div className="font-bold text-[#1A1A1A] mb-1">{label}</div>
      <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">
        {row.roundLabel}{row.sport ? ` · ${row.sport}` : ''}
      </div>
      <div className="space-y-1">
        {entries.map(e => (
          <div key={e.name} className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-sm inline-block" style={{ backgroundColor: e.color }} />
              <span className="font-semibold text-[#1A1A1A]">{e.name}</span>
            </div>
            <span className="font-bold tabular-nums text-[#1A1A1A]">
              {yKey === '__cum' ? e.value : (e.value > 0 ? `+${e.value}` : '0')}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Suppress the `label` prop shadowing warning
PointsTooltip.defaultProps = {};
